"use client";

import { useState, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  Cloud,
  BarChart3,
  Smartphone,
  CheckCircle,
  Star,
  Award,
  Users,
  School,
  GraduationCap,
  ArrowRight,
  IndianRupee,
  Clock,
  Target,
  Zap,
  Bus,
  Package,
  Truck,
  Warehouse,
} from "lucide-react";
import ERPLeadBot from "./ERPLeadBot";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Footer from "./Footer";

const Benefits = forwardRef((props, ref) => {
  const [activeProduct, setActiveProduct] = useState("school");
  const [activeTab, setActiveTab] = useState("schools");

  const benefits = [
    {
      icon: <IndianRupee className="h-6 w-6 text-green-600" />,
      title: "Affordable and Reasonable",
      description: "Starting at just ₹1 per student per month",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <Cloud className="h-6 w-6 text-blue-600" />,
      title: "Cloud based storage",
      description: "Secure, scalable, and accessible from anywhere",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <BarChart3 className="h-6 w-6 text-purple-600" />,
      title: "Data driven insights",
      description: "Make informed decisions with comprehensive analytics",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <Smartphone className="h-6 w-6 text-orange-600" />,
      title: "User friendly interface",
      description: "Intuitive design for all stakeholders",
      color: "from-orange-500 to-orange-600"
    }
  ];

  const allStakeholders = {
    school: {
      tabs: [
        {
          id: "schools",
          label: "Administrators",
          icon: School,
          title: "For School Administrators",
          features: [
            "Centralized dashboard for complete school oversight",
            "Comprehensive reporting and analytics tools",
            "Streamlined admission and enrollment processes",
            "Financial management and fee collection",
            "Staff management and HR tools",
          ],
          image: "/Schooldashboard.png",
          portalLabel: "Administrator Dashboard",
          description: "Get a bird's eye view of your entire institution with our intuitive admin dashboard.",
          bgColor: "bg-blue-50",
          accent: "text-blue-800",
          desc: "text-blue-700",
          blob: "bg-blue-100",
        },
        {
          id: "teachers",
          label: "Teachers",
          icon: GraduationCap,
          title: "For Teachers",
          features: [
            "Digital gradebooks and assessment tools",
            "Lesson planning and curriculum management",
            "Attendance tracking with automated reports",
            "Direct communication with students and parents",
            "Resource sharing and collaboration tools",
          ],
          image: "/TeacherBenifit.png",
          portalLabel: "Teacher Portal",
          description: "Simplify classroom management and focus on what matters most - teaching.",
          bgColor: "bg-blue-50",
          accent: "text-blue-800",
          desc: "text-blue-700",
          blob: "bg-blue-100",
        },
        {
          id: "students",
          label: "Parents/Students",
          icon: Users,
          title: "For Students & Parents",
          features: [
            "Access to assignments and study materials",
            "Real-time performance tracking",
            "Communication with teachers and peers",
            "Online submission of assignments and projects",
            "Attendance and behavior monitoring",
            "Access to school announcements and events",
          ],
          image: "/Student-Benifit.png",
          portalLabel: "Student & Parent Portal",
          description: "Stay connected with your child's education journey every step of the way.",
          bgColor: "bg-blue-50",
          accent: "text-blue-800",
          desc: "text-blue-700",
          blob: "bg-blue-100",
        },
      ],
    },
    bus: {
      tabs: [
        {
          id: "fleet-manager",
          label: "Fleet Manager",
          icon: Truck,
          title: "For Fleet Managers",
          features: [
            "Live GPS tracking for all vehicles on one map",
            "Route and stop management with drag-and-drop",
            "Driver duty scheduling and performance tracking",
            "Vehicle maintenance and document alerts",
            "Fuel usage and trip cost reports",
          ],
          image: "/Schooldashboard.png",
          portalLabel: "Fleet Manager Dashboard",
          description: "Full visibility and control over your entire fleet, from one screen.",
          bgColor: "bg-green-50",
          accent: "text-green-800",
          desc: "text-green-700",
          blob: "bg-green-100",
        },
        {
          id: "driver",
          label: "Drivers",
          icon: Bus,
          title: "For Drivers",
          features: [
            "Daily duty and route assignment on mobile",
            "Turn-by-turn navigation with stop list",
            "One-tap trip start and end recording",
            "SOS / emergency alert button",
            "Digital attendance for passengers",
          ],
          image: "/Schooldashboard.png",
          portalLabel: "Driver App",
          description: "Everything a driver needs, on a simple, easy-to-use mobile interface.",
          bgColor: "bg-green-50",
          accent: "text-green-800",
          desc: "text-green-700",
          blob: "bg-green-100",
        },
        {
          id: "bus-parents",
          label: "Parents",
          icon: Users,
          title: "For Parents",
          features: [
            "Track the bus live on map from phone",
            "Get notified when bus leaves and arrives",
            "See estimated arrival time at their stop",
            "View child's boarding and alighting status",
            "Receive delay and route change alerts",
          ],
          image: "/Student-Benifit.png",
          portalLabel: "Parent App",
          description: "Peace of mind for every parent, know where the bus is, always.",
          bgColor: "bg-green-50",
          accent: "text-green-800",
          desc: "text-green-700",
          blob: "bg-green-100",
        },
      ],
    },
    inventory: {
      tabs: [
        {
          id: "inv-manager",
          label: "Manager",
          icon: Package,
          title: "For Inventory Managers",
          features: [
            "Real-time stock levels across all locations",
            "Low-stock alerts and auto-reorder triggers",
            "Purchase order creation and approval workflow",
            "Supplier management and payment tracking",
            "Full audit trail for every stock movement",
          ],
          image: "/Schooldashboard.png",
          portalLabel: "Manager Dashboard",
          description: "Complete control over stock, suppliers, and procurement, from one dashboard.",
          bgColor: "bg-purple-50",
          accent: "text-purple-800",
          desc: "text-purple-700",
          blob: "bg-purple-100",
        },
        {
          id: "warehouse",
          label: "Warehouse Staff",
          icon: Warehouse,
          title: "For Warehouse Staff",
          features: [
            "Barcode-based item search and update",
            "Receive and record incoming stock quickly",
            "Issue items with digital records",
            "Physical stock audit with mobile app",
            "Real-time sync with central system",
          ],
          image: "/Schooldashboard.png",
          portalLabel: "Staff Interface",
          description: "Fast, simple tools for the floor, no training needed.",
          bgColor: "bg-purple-50",
          accent: "text-purple-800",
          desc: "text-purple-700",
          blob: "bg-purple-100",
        },
      ],
    },
  };

  const currentStakeholders = allStakeholders[activeProduct];
  const stakeholderFeatures = Object.fromEntries(
    currentStakeholders.tabs.map((t) => [t.id, t])
  );
  const firstTab = currentStakeholders.tabs[0].id;


  const animationVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div id="benefits" ref={ref} className="bg-white">
      {/* What Sets Us Apart Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-white relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-green-100 rounded-full opacity-20 animate-pulse"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center">
            <motion.div
              className="lg:w-1/2 mb-10 lg:mb-0"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={slideInLeft}
            >
              <div className="relative">
                <motion.img
                  src="/everythingknow.png"
                  alt="Dashboard"
                  width={600}
                  height={400}
                  className="rounded-lg shadow-xl w-full"
                  whileHover={{
                    scale: 1.03,
                    transition: { duration: 0.3 },
                  }}
                />
                <motion.div
                  className="absolute -bottom-5 -right-0 bg-white p-4 rounded-lg shadow-lg"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{
                        y: [0, -5, 0],
                      }}
                      transition={{
                        repeat: Number.POSITIVE_INFINITY,
                        duration: 2,
                      }}
                    >
                      <Shield className="h-8 w-8 text-blue-500" />
                    </motion.div>
                    <div>
                      <p className="text-sm font-medium">Secure & Reliable</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 lg:pl-12"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={slideInRight}
            >
              <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">NEXT GENERATION</Badge>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                <span className="text-blue-700">What sets us apart?</span>
              </h2>
              
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    variants={scaleIn}
                    className="flex items-center p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                    whileHover={{ y: -5 }}
                  >
                    <div className="p-3 bg-gray-50 rounded-lg mr-4">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">
                        {benefit.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* User-Specific Benefits */}
      <section className="py-16 sm:py-20 bg-gray-50 relative">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-10 sm:mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={animationVariants}
          >
            <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200 uppercase tracking-wider">
              USER-CENTRIC DESIGN
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Tailored for Every Stakeholder
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Each product is built around the people who use it, select a product to see who benefits.
            </p>

            {/* Product Selector */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { key: "school", label: "School Management", active: "bg-blue-600 text-white", inactive: "bg-white text-blue-600 border border-blue-200 hover:bg-blue-50" },
                { key: "bus", label: "Bus Management", active: "bg-green-600 text-white", inactive: "bg-white text-green-600 border border-green-200 hover:bg-green-50" },
                { key: "inventory", label: "Inventory Management", active: "bg-purple-600 text-white", inactive: "bg-white text-purple-600 border border-purple-200 hover:bg-purple-50" },
              ].map(({ key, label, active, inactive }) => (
                <button
                  key={key}
                  onClick={() => { setActiveProduct(key); setActiveTab(allStakeholders[key].tabs[0].id); }}
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${activeProduct === key ? active : inactive}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto"
          >
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={`grid mb-8 p-1 rounded-full w-full sm:w-fit mx-auto ${
                activeProduct === "school" ? "bg-blue-50" :
                activeProduct === "bus" ? "bg-green-50" : "bg-purple-50"
              }`}
                style={{ gridTemplateColumns: `repeat(${currentStakeholders.tabs.length}, minmax(0, 1fr))` }}
              >
                {currentStakeholders.tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  const activeCls = activeProduct === "school" ? "bg-blue-600 text-white" : activeProduct === "bus" ? "bg-green-600 text-white" : "bg-purple-600 text-white";
                  const inactiveCls = activeProduct === "school" ? "text-blue-600 hover:bg-blue-100" : activeProduct === "bus" ? "text-green-600 hover:bg-green-100" : "text-purple-600 hover:bg-purple-100";
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className={`rounded-full px-4 sm:px-6 py-2 transition-all duration-300 text-sm sm:text-base ${isActive ? activeCls : inactiveCls}`}
                    >
                      <tab.icon className="w-4 h-4 mr-2 inline-block" />
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {currentStakeholders.tabs.map((tab) => (
                    <TabsContent key={tab.id} value={tab.id} className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-gray-100">
                      <div className="flex flex-col lg:flex-row gap-8 items-center">
                        <div className="lg:w-1/2">
                          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">{tab.title}</h3>
                          <motion.ul className="space-y-4" variants={staggerContainer} initial="hidden" animate="visible">
                            {tab.features.map((item, index) => (
                              <motion.li
                                key={index}
                                className="flex items-start"
                                variants={animationVariants}
                                custom={index}
                                whileHover={{ x: 5, transition: { duration: 0.2 } }}
                              >
                                <div className={`${tab.bgColor} p-1 rounded-full mr-3 mt-0.5`}>
                                  <CheckCircle className={`h-5 w-5 ${
                                    activeProduct === "school" ? "text-blue-600" :
                                    activeProduct === "bus" ? "text-green-600" : "text-purple-600"
                                  }`} />
                                </div>
                                <p className="text-gray-700 text-sm sm:text-base">{item}</p>
                              </motion.li>
                            ))}
                          </motion.ul>
                        </div>
                        <div className="lg:w-1/2">
                          <motion.div
                            className={`${tab.bgColor} p-6 rounded-2xl relative overflow-hidden`}
                            whileHover={{ y: -5 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className={`absolute -right-10 -bottom-10 w-40 h-40 ${tab.blob} rounded-full opacity-50`}></div>
                            <h4 className={`text-xl font-bold ${tab.accent} mb-3`}>{tab.portalLabel}</h4>
                            <p className={`${tab.desc} mb-4 relative z-10 text-sm`}>{tab.description}</p>
                            <img
                              src={tab.image}
                              alt={tab.portalLabel}
                              width="1200"
                              height="750"
                              loading="lazy"
                              decoding="async"
                              className="rounded-lg shadow-lg border-4 border-white w-full relative z-10"
                            />
                          </motion.div>
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </motion.div>
              </AnimatePresence>
            </Tabs>
          </motion.div>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
});

Benefits.displayName = "Benefits";

export default Benefits;
